import axios from 'axios'

const apiClient = axios.create({
  baseURL: '/api',
  withCredentials: true,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    'X-Requested-With': 'XMLHttpRequest'
  }
})

function getCookie(name) {
  let cookieValue = null
  if (document.cookie && document.cookie !== '') {
    const cookies = document.cookie.split(';')
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i].trim()
      if (cookie.substring(0, name.length + 1) === `${name}=`) {
        cookieValue = decodeURIComponent(cookie.substring(name.length + 1))
        break
      }
    }
  }
  return cookieValue
}

apiClient.interceptors.request.use(config => {
  const csrfToken = getCookie('csrftoken')
  if (csrfToken) {
    config.headers['X-CSRFToken'] = csrfToken
  }
  return config
})

export default {
  get(url, config) {
    return apiClient.get(url, config)
  },
  post(url, data, config) {
    return apiClient.post(url, data, config)
  },
  put(url, data, config) {
    return apiClient.put(url, data, config)
  },
  patch(url, data, config) {
    return apiClient.patch(url, data, config)
  },
  delete(url, config) {
    return apiClient.delete(url, config)
  },

  checkAuth() {
    return apiClient.get('/users/check_auth/')
  },
  login(email, password, rememberMe = false) {
    return apiClient.post('/login/', { email, password, remember_me: rememberMe })
  },
  logout() {
    return apiClient.post('/logout/')
  },

  getUsers(params = {}) {
    return apiClient.get('/users/', { params })
  },
  createUser(data) {
    return apiClient.post('/users/', data)
  },
  updateUser(userId, data) {
    return apiClient.patch(`/users/${userId}/`, data)
  },

  getRegions(params = {}) {
    return apiClient.get('/regions/', { params })
  },
  createRegion(data) {
    return apiClient.post('/regions/', data)
  },
  updateRegion(regionId, data) {
    return apiClient.patch(`/regions/${regionId}/`, data)
  },
  deleteRegion(regionId) {
    return apiClient.delete(`/regions/${regionId}/`)
  },

  getDistricts(params = {}) {
    return apiClient.get('/districts/', { params })
  },
  createDistrict(data) {
    return apiClient.post('/districts/', data)
  },
  updateDistrict(districtId, data) {
    return apiClient.patch(`/districts/${districtId}/`, data)
  },
  deleteDistrict(districtId) {
    return apiClient.delete(`/districts/${districtId}/`)
  },

  getClinics(params = {}) {
    return apiClient.get('/clinics/', { params })
  },
  createClinic(data) {
    return apiClient.post('/clinics/', data)
  },
  updateClinic(clinicId, data) {
    return apiClient.patch(`/clinics/${clinicId}/`, data)
  },
  deleteClinic(clinicId) {
    return apiClient.delete(`/clinics/${clinicId}/`)
  },

  getDoctors(params = {}) {
    return apiClient.get('/doctors/', { params })
  },
  createDoctor(data, config = {}) {
    return apiClient.post('/doctors/', data, config)
  },
  updateDoctor(doctorId, data, config = {}) {
    return apiClient.patch(`/doctors/${doctorId}/`, data, config)
  },
  deleteDoctor(doctorId) {
    return apiClient.delete(`/doctors/${doctorId}/`)
  },

  getSpecializations(params = {}) {
    return apiClient.get('/specializations/', { params })
  },

  getSurveys(params = {}) {
    return apiClient.get('/surveys/surveys/', { params })
  },
  getSurvey(surveyId) {
    return apiClient.get(`/surveys/surveys/${surveyId}/`)
  },
  createSurvey(surveyData) {
    return apiClient.post('/surveys/surveys/', surveyData)
  },
  updateSurvey(surveyId, surveyData) {
    return apiClient.put(`/surveys/surveys/${surveyId}/`, surveyData)
  },
  deleteSurvey(surveyId) {
    return apiClient.delete(`/surveys/surveys/${surveyId}/`)
  },
  publishSurvey(surveyId) {
    return apiClient.post(`/surveys/surveys/${surveyId}/publish/`)
  },
  unpublishSurvey(surveyId) {
    return apiClient.post(`/surveys/surveys/${surveyId}/unpublish/`)
  },
  duplicateSurvey(surveyId) {
    return apiClient.post(`/surveys/surveys/${surveyId}/duplicate/`)
  },

  getSurveyQuestions(surveyId) {
    return apiClient.get('/surveys/questions/', { params: { survey: surveyId } })
  },
  createQuestion(questionData) {
    return apiClient.post('/surveys/questions/', questionData)
  },
  updateQuestion(questionId, questionData) {
    return apiClient.put(`/surveys/questions/${questionId}/`, questionData)
  },
  deleteQuestion(questionId) {
    return apiClient.delete(`/surveys/questions/${questionId}/`)
  },

  getSurveyStatistics(surveyId) {
    return apiClient.get(`/surveys/surveys/${surveyId}/statistics/`)
  },
  getSurveyResponses(params = {}) {
    return apiClient.get('/surveys/responses/', { params })
  },
  getSurveyResponse(responseId) {
    return apiClient.get(`/surveys/responses/${responseId}/`)
  },
  getPatientSurveyResponses(patientId) {
    return apiClient.get('/surveys/responses/', { params: { patient: patientId } })
  },

  getAvailableSurveys(lang = 'ru') {
    return apiClient.get('/surveys/surveys/available/', { params: { lang } })
  },
  takeSurvey(surveyId) {
    return apiClient.get(`/surveys/surveys/${surveyId}/take/`)
  },
  submitSurvey(surveyId, answers, note = '', lang = undefined) {
    const params = lang ? { lang } : {}
    return apiClient.post(`/surveys/surveys/${surveyId}/submit/`, { answers, note }, { params })
  },
  getDoctorSurveyHistory(params = {}) {
    return apiClient.get('/surveys/responses/', { params })
  },

  getRiskDashboard(params = {}) {
    return apiClient.get('/surveys/risk-dashboard/', { params })
  },
  getRiskAssessment(responseId, lang = undefined) {
    const params = lang ? { lang } : {}
    return apiClient.get(`/surveys/responses/${responseId}/risk_assessment/`, { params })
  },

  getMobileConfig(lang = 'ru') {
    return apiClient.get('/mobile/config/', { params: { lang } })
  },
  getMobileContent(resource, includeInactive = false, params = {}) {
    return apiClient.get(`/mobile/${resource}/`, {
      params: { ...params, include_inactive: includeInactive ? 'true' : undefined }
    })
  },
  createMobileContent(resource, data, config = {}) {
    return apiClient.post(`/mobile/${resource}/`, data, config)
  },
  updateMobileContent(resource, id, data, config = {}) {
    return apiClient.patch(`/mobile/${resource}/${id}/`, data, config)
  }
}
